// =============================================================================
// ui/state/snapshot.tsx — the header camera.
// -----------------------------------------------------------------------------
// The button lives in the toolbar and the region it captures lives in a TAB, so
// something has to join them. This is that seam.
//
// ── ⚠ WHY A CONTEXT AND NOT `insertBefore` ──
//
// The old client injects an ephemeral title bar into the capture region with
// `region.insertBefore(...)` and removes it in a `finally`. That is correct for
// a client that owns its own DOM, and it is not safe here: the region is React's,
// the capture is `await`ed, and any re-render inside that window leaves React
// reconciling children it does not know about — an untracked first child moves
// every sibling index it inserts against.
//
// So the title bar is a RENDERED node the region owns, shown only while
// `capturing` is that region's id. The shell asks; the tab draws.
//
// ── ⚠ NO PERSISTENCE, AND THAT IS BETA'S CURRENT BEHAVIOUR ──
//
// The old client used to write the PNG to a Storage bucket and index it in a
// `snapshots` table. Both are gone — its own comment says "the Snapshots archive
// was retired" — and it now opens the blob in a new tab. Porting the retired
// version because the table still exists would rebuild something its author
// deliberately removed.
// =============================================================================

import {
  createContext, useCallback, useContext, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { reportFailureAs } from '../../io/diagnostics/errorLog';

export type SnapshotPhase = 'idle' | 'capturing' | 'done' | 'failed' | 'blocked';

export interface SnapshotState {
  /** The region id currently being captured, or null. Tabs read this. */
  capturing: string | null;
  phase: SnapshotPhase;
  message: string | null;
  /** Null when the active tab offers no snapshot — the button disables itself. */
  target: SnapshotTarget | null;
  /** Registered by whichever tab can be captured. */
  setTarget: (t: SnapshotTarget | null) => void;
  capture: () => void;
}

export interface SnapshotTarget {
  /** The DOM id of the region to rasterize. */
  regionId: string;
  /** The right-hand caption in the injected title bar — beta's date-range label. */
  title: string;
  /** Used only in the failure message, so a user can say which one broke. */
  label: string;
}

/**
 * A 1×1 transparent PNG, handed to the rasterizer as `imagePlaceholder`.
 * See the `cacheBust` note at the capture call for why both exist.
 */
const TRANSPARENT_PX =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk'
  + '+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

/**
 * A human sentence for whatever the rasterizer rejected with.
 *
 * ⚠ IT IS OFTEN NOT AN `Error`. An image that fails to load rejects with a DOM
 * `Event`, which has no `.message` — so the obvious
 * `e instanceof Error ? e.message : String(e)` printed `[object Event]`, which
 * tells the user nothing and told beta's author "network problem" for an hour.
 * Name the element instead. See #42.
 */
export function rasterErrorText(e: unknown): string {
  if (e instanceof Error && e.message) return e.message;
  const t = (e as { target?: unknown } | null)?.target as
    { tagName?: string; className?: unknown; alt?: string } | undefined;
  if (t && (t.tagName === 'IMG' || t.tagName === 'IMAGE')) {
    const what = (typeof t.className === 'string' && t.className) || t.alt || 'an image';
    return `an image on the page could not be loaded for the export (${what})`;
  }
  const type = (e as { type?: unknown } | null)?.type;
  if (typeof type === 'string' && type) return `the export failed (${type})`;
  return 'the export failed for an unknown reason';
}

const Ctx = createContext<SnapshotState | null>(null);

export function useSnapshot(): SnapshotState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSnapshot() outside <SnapshotProvider>');
  return v;
}

/** Two frames, so the title bar has actually PAINTED before the raster runs. */
const nextPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

export function SnapshotProvider(
  { children, toBlob }:
  {
    children: ReactNode;
    /** Injectable so a test never loads a rasterizer into jsdom. */
    toBlob?: (node: HTMLElement, opts: Record<string, unknown>) => Promise<Blob | null>;
  },
) {
  const [capturing, setCapturing] = useState<string | null>(null);
  const [phase, setPhase] = useState<SnapshotPhase>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [target, setTarget] = useState<SnapshotTarget | null>(null);

  // ⚠ A ref, not state: a second click while a capture is in flight must be
  // ignored, and reading `phase` from the closure would read the value captured
  // at the time the handler was created.
  const busy = useRef(false);

  const capture = useCallback(() => {
    if (busy.current || !target) return;
    busy.current = true;

    void (async () => {
      setPhase('capturing');
      setMessage(null);
      setCapturing(target.regionId);
      try {
        await nextPaint();
        const region = document.getElementById(target.regionId);
        if (!region) throw new Error(`region #${target.regionId} is not on the page`);

        const raster = toBlob ?? (await import('html-to-image')).toBlob;
        const blob = await raster(region, {
          // 2× for a retina-quality PNG, matching the old client.
          pixelRatio: 2,
          // ⚠ Explicit background. A transparent PNG dropped into Discord renders
          // white-on-white text, which is how a dark-theme capture becomes
          // unreadable without ever looking broken locally.
          backgroundColor: getComputedStyle(document.body).backgroundColor || '#050507',
          // ── 🚨 `cacheBust` MUST STAY FALSE, AND THIS IS NOT A PREFERENCE ──
          //
          // html-to-image inlines every <img> by fetching its src. With
          // cacheBust it first appends `?<timestamp>` — and it skips `data:`
          // URLs when deciding whether to touch one, but NOT `blob:` URLs.
          // A blob URL with a query string does not exist:
          //
          //   blob:https://host/01286b1c-…?1785774048197 → ERR_FILE_NOT_FOUND
          //
          // The library swallows that, sets `img.src = ''`, and the empty src
          // fires an `error` EVENT that rejects the whole capture. One chart
          // screenshot on the page killed the entire PNG.
          //
          // ⚠ It bought nothing anyway: a blob URL is unique per
          // `createObjectURL`, so there is no stale cache to defeat.
          //
          // Beta shipped this bug and paid for it — Josh reported it 2026-08-03
          // as "Post to Discord fails". It was never Discord; the PNG never got
          // built. This client re-introduced both halves. See #42.
          cacheBust: false,
          // ⚠ The second half: an image that genuinely cannot be inlined
          // degrades to a transparent pixel instead of destroying the export.
          // One unreadable screenshot must not cost the whole picture.
          imagePlaceholder: TRANSPARENT_PX,
        });
        if (!blob) throw new Error('the rasterizer returned nothing');

        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank');
        if (!win) {
          // ⚠ NOT an error. Nothing failed — the browser declined to open a tab,
          // and the fix is the user's to make.
          setPhase('blocked');
          setMessage('Pop-up blocked — allow pop-ups for this site to open the snapshot.');
        } else {
          setPhase('done');
          setMessage('Snapshot opened in a new tab.');
        }
        // ⚠ Revoked LATE. Revoking straight away races the new tab's load and
        // yields a broken image with no error anywhere.
        setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* gone already */ } }, 60_000);
      } catch (e) {
        setPhase('failed');
        // ⚠ Through the funnel, so a failed export reaches the activity log
        // rather than living only in this component's own status line — which
        // disappears the moment the user changes tab. `reportFailureAs` because
        // `rasterErrorText` describes a DOM Event far better than `String(e)`,
        // which would record `[object Event]`.
        setMessage(reportFailureAs('snapshot', e, rasterErrorText(e)));
      } finally {
        // ⚠ ALWAYS clears, so a failed capture can never leave the title bar on
        // screen — the reason the old client removes it in a `finally` too.
        setCapturing(null);
        busy.current = false;
      }
    })();
  }, [target, toBlob]);

  const value = useMemo<SnapshotState>(
    () => ({ capturing, phase, message, target, setTarget, capture }),
    [capturing, phase, message, target, capture],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
